/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Provides access to the AAS DPP (Digital Product Passport) API of the
 * aas-connector service.
 *
 * HTTP methods migrated to ApiClient (Observable-based):
 * - `createDpp$()`                  — POST /api/aas/dpp/shells  (Pattern A)
 * - `uploadThumbnail$()`            — PUT  …/thumbnail  (Pattern A, FormData)
 * - `getPassports$()`               — GET  …/shell-descriptors  (Pattern A)
 * - `getDppShell$()`                — GET  …/shells/:id  (Pattern A)
 * - `getThumbnail$()`               — GET  …/thumbnail  (Pattern A, binary)
 * - `deleteDppShell$()`             — DELETE …/shells/:id  (Pattern A)
 * - `getSubmodel$()`                — GET  …/submodels/:id  (Pattern A)
 * - `createSubmodel$()`             — POST …/submodels  (Pattern A)
 * - `updateSubmodel$()`             — PUT  …/submodels/:id  (Pattern A)
 * - `getHandoverDocuments$()`       — delegates to `getSubmodel$()`, maps result
 * - `addSubmodelAttachment$()`      — PUT  …/attachment  (Pattern A, FormData)
 * - `getSubmodelAttachments$()`     — parallel GET via forkJoin  (Pattern A)
 * - `deleteSubmodelAttachments$()`  — parallel DELETE via forkJoin  (Pattern A)
 * - `getDppShellPublic$()`          — GET  …/public/shells/:id  (Pattern A)
 * - `getSubmodelPublic$()`          — GET  …/public/submodels/:id  (Pattern A)
 *
 * Deprecated Promise wrappers (backward-compat, delegate to firstValueFrom):
 * - `createDpp()`, `uploadThumbnail()`, `getPassports()`, `getDppShell()",
 *   `getThumbnail()`, `deleteDppShell()`, `getSubmodel()`, `createSubmodel()",
 *   `updateSubmodel()`, `getHandoverDocuments()`, `addSubmodelAttachment()",
 *   `getSubmodelAttachments()`, `deleteSubmodelAttachments()",
 *   `getDppShellPublic()`, `getSubmodelPublic()`
 */

import {inject, Injectable} from '@angular/core';
import {firstValueFrom, forkJoin, map, Observable, of} from 'rxjs';

import {environment} from '../environments/environment';
import {ApiClient} from './api-client.service';
import {CreateShellRequest} from '../models/dpp/CreateShellRequest';
import {ListShellDescriptor} from '../models/dpp/list-shell-descriptor';
import {LoadListParams} from '../models/dpp/search-criteria';
import {ShellDescriptor} from '../models/dpp/shell-descriptor';
import {ShellDetail} from '../models/dpp/SubmodelReference';
import {Submodel} from '@product-passport/dpp/model';
import {DocumentMetadata} from '../models/dpp/document-metadata';
import {HandoverDocument} from '../models/dpp/handover-document';
import {parseHandoverDocumentsSubmodel} from '../app/components/dpp/handover-documentation/utils/handover-document-parser';
import {FileResponse} from '../models/file-response';

/** Base URL of the AAS connector service. */
const AAS_HOST = environment.backend.aas_connector_host;

/** Raw paginated response shape from the shell-descriptors endpoint. */
interface ShellDescriptorListResponse {
  pagingMetadata?: {cursor: string};
  cursor?: string;
  result: Array<{
    id: string;
    globalAssetId?: string;
    displayName?: Array<{language: string; text: string}>;
  }>;
}

@Injectable({
  providedIn: 'root',
})
export class DppService {
  private readonly apiClient = inject(ApiClient);

  // ─── Private helpers ─────────────────────────────────────────────────────────

  private encodeToBase64(value: string): string {
    return btoa(value);
  }

  private buildPassportsUrl(params?: LoadListParams): string {
    const queryParams = new URLSearchParams();
    if (params?.passportName) {
      queryParams.append('displayName', params.passportName);
    }
    if (params?.aasId) {
      queryParams.append('aasId', params.aasId);
    }
    if (params?.globalAssetId) {
      queryParams.append('globalAssetId', params.globalAssetId);
    }
    if (params?.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.cursor) {
      queryParams.append('cursor', params.cursor);
    }
    const url = new URL(`${AAS_HOST}/api/aas/dpp/shell-descriptors`);
    url.search = queryParams.toString();
    return url.toString();
  }

  // ─── Observable API (new) ────────────────────────────────────────────────────

  /**
   * Creates a new AAS Digital Product Passport shell.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param body - Request payload for creating a DPP shell.
   * @returns An `Observable` emitting the created `ShellDescriptor`.
   */
  createDpp$(body: CreateShellRequest): Observable<ShellDescriptor> {
    return this.apiClient.post<ShellDescriptor>(
      `${AAS_HOST}/api/aas/dpp/shells`,
      body,
    );
  }

  /**
   * Uploads a thumbnail image for an existing DPP shell.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param aasId - The AAS identifier.
   * @param file  - The thumbnail image file.
   * @returns An `Observable` that completes on success.
   */
  uploadThumbnail$(aasId: string, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file);
    const encodedId = this.encodeToBase64(aasId);
    return this.apiClient.put<void>(
      `${AAS_HOST}/api/aas/dpp/shells/${encodedId}/asset-information/thumbnail`,
      formData,
    );
  }

  /**
   * Retrieves all available passports with cursor-based pagination.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param params - Optional search / pagination criteria.
   * @returns An `Observable` emitting a `ListShellDescriptor`.
   */
  getPassports$(params?: LoadListParams): Observable<ListShellDescriptor> {
    return this.apiClient
      .get<ShellDescriptorListResponse>(this.buildPassportsUrl(params))
      .pipe(
        map((response) => {
          const cursor = response.pagingMetadata?.cursor ?? response.cursor;

          return {
            data: response.result,
            metadata: cursor ? {cursor} : undefined,
          };
        }),
      );
  }

  /**
   * Retrieves a DPP shell by AAS ID with detailed submodel information.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param aasId - The AAS identifier.
   * @returns An `Observable` emitting the `ShellDetail`.
   */
  getDppShell$(aasId: string): Observable<ShellDetail> {
    const encodedId = this.encodeToBase64(aasId);
    return this.apiClient.get<ShellDetail>(
      `${AAS_HOST}/api/aas/dpp/shells/${encodedId}`,
    );
  }

  /**
   * Retrieves the thumbnail image for a DPP shell.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param aasId - The AAS identifier.
   * @returns An `Observable` emitting the thumbnail as a `FileResponse`.
   */
  getThumbnail$(aasId: string): Observable<FileResponse> {
    const encodedId = this.encodeToBase64(aasId);
    return this.apiClient.getFile(
      `${AAS_HOST}/api/aas/dpp/public/shells/${encodedId}/asset-information/thumbnail`,
    );
  }

  /**
   * Deletes a DPP shell by AAS ID.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param aasId - The AAS identifier.
   * @returns An `Observable` that completes on success.
   */
  deleteDppShell$(aasId: string): Observable<void> {
    const encodedId = this.encodeToBase64(aasId);
    return this.apiClient.delete<void>(
      `${AAS_HOST}/api/aas/dpp/shells/${encodedId}`,
    );
  }

  /**
   * Retrieves a specific submodel by submodel ID.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param submodelId - The submodel identifier.
   * @returns An `Observable` emitting the `Submodel`.
   */
  getSubmodel$(submodelId: string): Observable<Submodel> {
    const encodedId = this.encodeToBase64(submodelId);
    return this.apiClient.get<Submodel>(
      `${AAS_HOST}/api/aas/dpp/submodels/${encodedId}`,
    );
  }

  /**
   * Creates a new submodel for a given AAS ID.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param aasId    - The AAS identifier.
   * @param submodel - The `Submodel` to create.
   * @returns An `Observable` emitting the created `Submodel`.
   */
  createSubmodel$(aasId: string, submodel: Submodel): Observable<Submodel> {
    const encodedAasId = this.encodeToBase64(aasId);
    return this.apiClient.post<Submodel>(
      `${AAS_HOST}/api/aas/dpp/submodels?aasIdentifier=${encodedAasId}`,
      submodel,
    );
  }

  /**
   * Updates an existing submodel by replacing it entirely.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param submodel - The updated `Submodel` to persist.
   * @returns An `Observable` that completes on success.
   */
  updateSubmodel$(submodel: Submodel): Observable<void> {
    // update this once the backend supports the update of submodels by submodel id base64 encoded
    // const encodedId = this.encodeToBase64(submodel.id);
    return this.apiClient.put<void>(
      `${AAS_HOST}/api/aas/dpp/submodels/${submodel.id}`,
      submodel,
    );
  }

  /**
   * Retrieves and parses the handover-documents submodel.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param submodelId - The submodel identifier.
   * @returns An `Observable` emitting the parsed `HandoverDocument[]`.
   */
  getHandoverDocuments$(submodelId: string): Observable<HandoverDocument[]> {
    return this.getSubmodel$(submodelId).pipe(
      map((submodel) => parseHandoverDocumentsSubmodel(submodel)),
    );
  }

  /**
   * Adds a document attachment to a submodel with metadata as multipart form
   * data.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param submodelId       - The submodel identifier.
   * @param documentMetadata - The document metadata.
   * @param file             - The document file to upload.
   * @returns An `Observable` that completes on success.
   */
  addSubmodelAttachment$(
    submodelId: string,
    documentMetadata: DocumentMetadata,
    file: File,
  ): Observable<void> {
    const encodedId = this.encodeToBase64(submodelId);
    const formData = new FormData();
    formData.append('metadata', JSON.stringify(documentMetadata));
    formData.append('file', file);
    return this.apiClient.put<void>(
      `${AAS_HOST}/api/aas/dpp/submodels/${encodedId}/attachment`,
      formData,
    );
  }

  /**
   * Retrieves a list of attachments from a submodel by their idShorts.
   *
   * All downloads are issued in parallel via `forkJoin`. Errors propagate to
   * `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param submodelId - The submodel identifier.
   * @param idShorts   - List of attachment idShorts to retrieve.
   * @returns An `Observable` emitting the `FileResponse[]`.
   */
  getSubmodelAttachments$(
    submodelId: string,
    idShorts: string[],
  ): Observable<FileResponse[]> {
    if (idShorts.length === 0) {
      return of([]);
    }
    const encodedId = this.encodeToBase64(submodelId);
    return forkJoin(
      idShorts.map((idShort) =>
        this.apiClient.getFile(
          `${AAS_HOST}/api/aas/dpp/submodels/${encodedId}/attachment/${idShort}`,
        ),
      ),
    );
  }

  /**
   * Deletes a list of attachments from a submodel by their idShorts.
   *
   * All deletes are issued in parallel via `forkJoin`. Errors propagate to
   * `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param submodelId - The submodel identifier.
   * @param idShorts   - List of attachment idShorts to delete.
   * @returns An `Observable` that completes when all deletes succeed.
   */
  deleteSubmodelAttachments$(
    submodelId: string,
    idShorts: string[],
  ): Observable<void[]> {
    if (idShorts.length === 0) {
      return of([]);
    }
    const encodedId = this.encodeToBase64(submodelId);
    return forkJoin(
      idShorts.map((idShort) =>
        this.apiClient.delete<void>(
          `${AAS_HOST}/api/aas/dpp/submodels/${encodedId}/attachment/${idShort}`,
        ),
      ),
    );
  }

  /**
   * Retrieves a DPP shell by AAS ID without authentication (public endpoint).
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param aasId - The AAS identifier.
   * @returns An `Observable` emitting the `ShellDetail`.
   */
  getDppShellPublic$(aasId: string): Observable<ShellDetail> {
    const encodedId = this.encodeToBase64(aasId);
    return this.apiClient.get<ShellDetail>(
      `${AAS_HOST}/api/aas/dpp/public/shells/${encodedId}`,
    );
  }

  /**
   * Retrieves the submodel by submodel ID without authentication (public
   * endpoint).
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param submodelId - The submodel identifier.
   * @returns An `Observable` emitting the `Submodel`.
   */
  getSubmodelPublic$(submodelId: string): Observable<Submodel> {
    const encodedId = this.encodeToBase64(submodelId);
    return this.apiClient.get<Submodel>(
      `${AAS_HOST}/api/aas/dpp/public/submodels/${encodedId}`,
    );
  }

  // ─── Deprecated Promise wrappers (backward-compat) ───────────────────────────

  /** @deprecated Use `createDpp$()` instead. */
  async createDpp(body: CreateShellRequest): Promise<ShellDescriptor> {
    return firstValueFrom(this.createDpp$(body));
  }

  /** @deprecated Use `uploadThumbnail$()` instead. */
  async uploadThumbnail(aasId: string, file: File): Promise<void> {
    return firstValueFrom(this.uploadThumbnail$(aasId, file));
  }

  /** @deprecated Use `getPassports$()` instead. */
  async getPassports(params?: LoadListParams): Promise<ListShellDescriptor> {
    return firstValueFrom(this.getPassports$(params));
  }

  /** @deprecated Use `getDppShell$()` instead. */
  async getDppShell(aasId: string): Promise<ShellDetail> {
    return firstValueFrom(this.getDppShell$(aasId));
  }

  /** @deprecated Use `getThumbnail$()` instead. */
  async getThumbnail(aasId: string): Promise<FileResponse> {
    return firstValueFrom(this.getThumbnail$(aasId));
  }

  /** @deprecated Use `deleteDppShell$()` instead. */
  async deleteDppShell(aasId: string): Promise<void> {
    return firstValueFrom(this.deleteDppShell$(aasId));
  }

  /** @deprecated Use `getSubmodel$()` instead. */
  async getSubmodel(submodelId: string): Promise<Submodel> {
    return firstValueFrom(this.getSubmodel$(submodelId));
  }

  /** @deprecated Use `createSubmodel$()` instead. */
  async createSubmodel(aasId: string, submodel: Submodel): Promise<Submodel> {
    return firstValueFrom(this.createSubmodel$(aasId, submodel));
  }

  /** @deprecated Use `updateSubmodel$()` instead. */
  async updateSubmodel(submodel: Submodel): Promise<void> {
    return firstValueFrom(this.updateSubmodel$(submodel));
  }

  /** @deprecated Use `getHandoverDocuments$()` instead. */
  async getHandoverDocuments(submodelId: string): Promise<HandoverDocument[]> {
    return firstValueFrom(this.getHandoverDocuments$(submodelId));
  }

  /** @deprecated Use `addSubmodelAttachment$()` instead. */
  async addSubmodelAttachment(
    submodelId: string,
    documentMetadata: DocumentMetadata,
    file: File,
  ): Promise<void> {
    return firstValueFrom(
      this.addSubmodelAttachment$(submodelId, documentMetadata, file),
    );
  }

  /** @deprecated Use `getSubmodelAttachments$()` instead. */
  async getSubmodelAttachments(
    submodelId: string,
    idShorts: string[],
  ): Promise<FileResponse[]> {
    return firstValueFrom(this.getSubmodelAttachments$(submodelId, idShorts));
  }

  /** @deprecated Use `deleteSubmodelAttachments$()` instead. */
  async deleteSubmodelAttachments(
    submodelId: string,
    idShorts: string[],
  ): Promise<void> {
    await firstValueFrom(this.deleteSubmodelAttachments$(submodelId, idShorts));
  }

  /** @deprecated Use `getDppShellPublic$()` instead. */
  async getDppShellPublic(aasId: string): Promise<ShellDetail> {
    return firstValueFrom(this.getDppShellPublic$(aasId));
  }

  /** @deprecated Use `getSubmodelPublic$()` instead. */
  async getSubmodelPublic(submodelId: string): Promise<Submodel> {
    return firstValueFrom(this.getSubmodelPublic$(submodelId));
  }
}
